import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { UserService, UserModel, RoleModel, ProjectUserModel } from '../../services/user.service';
import { ProjectService } from '../../services/project.service';
import { ProjectStateService } from '../../services/project-state.service';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntil, finalize, map, catchError } from 'rxjs/operators';

interface UserAssignment {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  roleId: number;
  originalRoleId: number;
  isProcessing?: boolean;
}

@Component({
  selector: 'app-assign-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    MultiSelectModule,
    SelectModule
  ],
  templateUrl: './assign-users.html',
  styleUrls: ['./assign-users.css']
})
export class AssignUsers implements OnInit, OnDestroy {
  projectId!: number;
  allUsers: UserModel[] = [];
  availableUsers: UserModel[] = [];
  roles: RoleModel[] = [];
  userAssignments: UserAssignment[] = [];
  selectedUserIds: number[] = [];
  previousSelectedUserIds: number[] = [];
  processingUserIds = new Set<number>();
  dataLoaded = false;
  isSaving = false;
  isEditMode = false;
  editUser: ProjectUserModel | null = null;
  private destroy$ = new Subject<void>();

  get isProcessingAny(): boolean {
    return this.processingUserIds.size > 0;
  }

  get hasPendingRoleChanges(): boolean {
    return this.userAssignments.some(assignment => assignment.roleId !== assignment.originalRoleId);
  }

  constructor(
    private userService: UserService,
    private projectService: ProjectService,
    private projectStateService: ProjectStateService,
    private messageService: MessageService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.projectId = this.config.data?.projectId;
    this.isEditMode = this.config.data?.isEditMode || false;
    this.editUser = this.config.data?.user || null;

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required'
      });
      this.ref.close();
      return;
    }

    setTimeout(() => {
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    if (this.isEditMode && this.editUser) {
      forkJoin({
        users: this.userService.getUserList(),
        roles: this.projectService.getRoleList()
      }).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.dataLoaded = true;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: ({ users, roles }) => {
          this.allUsers = users || [];
          this.roles = roles || [];
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading edit mode data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load user data'
          });
        }
      });
    } else {
      // Add mode: load users, roles, and existing project users
      forkJoin({
        users: this.userService.getUserList(),
        roles: this.projectService.getRoleList(),
        projectUsers: this.userService.getProjectUserList(this.projectId)
      }).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.dataLoaded = true;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: ({ users, roles, projectUsers }) => {
          this.allUsers = users || [];
          this.roles = roles || [];

          // Populate userAssignments with existing project users
          if (projectUsers && projectUsers.length > 0) {
            this.userAssignments = projectUsers.map(pu => ({
              userId: pu.userId!,
              email: pu.email,
              firstName: pu.firstName,
              lastName: pu.lastName,
              roleId: pu.roleId,
              originalRoleId: pu.roleId
            }));

            // Populate selectedUserIds to show them checked in dropdown
            this.selectedUserIds = projectUsers.map(pu => pu.userId!);
            this.previousSelectedUserIds = [...this.selectedUserIds];
          }

          // Set available users with display labels
          this.updateAvailableUsers();

          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load user data'
          });
        }
      });
    }
  }

  updateAvailableUsers(): void {
    this.availableUsers = this.allUsers
      .map(user => ({
        ...user,
        displayLabel: `${user.firstName} ${user.lastName} (${user.email})`
      } as any))
      .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }

  onUserSelectionChange(): void {
    const added = this.selectedUserIds.filter(id => !this.previousSelectedUserIds.includes(id));
    const removed = this.previousSelectedUserIds.filter(id => !this.selectedUserIds.includes(id));

    added.forEach(userId => this.assignUser(userId));
    removed.forEach(userId => this.unassignUser(userId));
  }

  private assignUser(userId: number): void {
    if (this.processingUserIds.has(userId)) return;

    const user = this.allUsers.find(u => u.userId === userId);
    if (!user) return;

    const assignableRoles = this.getAssignableRolesForUser(userId);
    const defaultRole = assignableRoles.find(r => this.normalize(r.roleName) === 'user') || assignableRoles[0];

    if (!defaultRole) {
      this.selectedUserIds = this.selectedUserIds.filter(id => id !== userId);
      this.previousSelectedUserIds = [...this.selectedUserIds];
      this.messageService.add({
        severity: 'warn',
        summary: 'No Allowed Role',
        detail: `${user.email} cannot be assigned because no project roles are available for their user type/role.`
      });
      this.cdr.detectChanges();
      return;
    }

    this.processingUserIds.add(userId);
    this.cdr.detectChanges();

    this.projectService.addProjectUsers(this.projectId, [{ userId, roleId: defaultRole.roleId }]).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (success) => {
        if (success) {
          this.userAssignments.push({
            userId: user.userId!,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            roleId: defaultRole.roleId,
            originalRoleId: defaultRole.roleId
          });
          this.previousSelectedUserIds = [...this.selectedUserIds];
          this.messageService.add({
            severity: 'success',
            summary: 'User Added',
            detail: `${user.email} has been assigned to the project`
          });
          this.notifyProjectUsersChanged();
        } else {
          // Revert checkbox on failure
          this.selectedUserIds = this.selectedUserIds.filter(id => id !== userId);
          this.previousSelectedUserIds = [...this.selectedUserIds];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to assign user. Please try again.'
          });
        }
        this.processingUserIds.delete(userId);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error assigning user:', error);
        // Revert checkbox on error
        this.selectedUserIds = this.selectedUserIds.filter(id => id !== userId);
        this.previousSelectedUserIds = [...this.selectedUserIds];
        this.processingUserIds.delete(userId);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to assign user. Please try again.'
        });
        this.cdr.detectChanges();
      }
    });
  }

  private unassignUser(userId: number): void {
    if (this.processingUserIds.has(userId)) return;

    const assignment = this.userAssignments.find(ua => ua.userId === userId);
    if (!assignment) return;

    this.processingUserIds.add(userId);
    assignment.isProcessing = true;
    this.cdr.detectChanges();

    this.projectService.deleteProjectUser(this.projectId, userId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (success) => {
        if (success) {
          this.userAssignments = this.userAssignments.filter(ua => ua.userId !== userId);
          this.previousSelectedUserIds = [...this.selectedUserIds];
          this.messageService.add({
            severity: 'success',
            summary: 'User Removed',
            detail: `${assignment.email} has been removed from the project`
          });
          this.notifyProjectUsersChanged();
        } else {
          // Revert checkbox on failure
          this.selectedUserIds = [...this.selectedUserIds, userId];
          this.previousSelectedUserIds = [...this.selectedUserIds];
          assignment.isProcessing = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove user. Please try again.'
          });
        }
        this.processingUserIds.delete(userId);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error removing user:', error);
        // Revert checkbox on error
        this.selectedUserIds = [...this.selectedUserIds, userId];
        this.previousSelectedUserIds = [...this.selectedUserIds];
        assignment.isProcessing = false;
        this.processingUserIds.delete(userId);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove user. Please try again.'
        });
        this.cdr.detectChanges();
      }
    });
  }

  removeUser(index: number): void {
    const removedUser = this.userAssignments[index];
    if (!removedUser || this.processingUserIds.has(removedUser.userId)) return;

    this.processingUserIds.add(removedUser.userId);
    removedUser.isProcessing = true;
    this.cdr.detectChanges();

    this.projectService.deleteProjectUser(this.projectId, removedUser.userId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (success) => {
        if (success) {
          this.userAssignments.splice(index, 1);
          this.selectedUserIds = this.selectedUserIds.filter(id => id !== removedUser.userId);
          this.previousSelectedUserIds = this.previousSelectedUserIds.filter(id => id !== removedUser.userId);
          this.messageService.add({
            severity: 'success',
            summary: 'User Removed',
            detail: `${removedUser.email} has been removed from the project`
          });
          this.notifyProjectUsersChanged();
        } else {
          removedUser.isProcessing = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove user. Please try again.'
          });
        }
        this.processingUserIds.delete(removedUser.userId);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error removing user:', error);
        removedUser.isProcessing = false;
        this.processingUserIds.delete(removedUser.userId);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove user. Please try again.'
        });
        this.cdr.detectChanges();
      }
    });
  }

  saveAllRoleChanges(): void {
    if (this.isEditMode || this.isSaving) return;

    const changedAssignments = this.userAssignments.filter(
      assignment => assignment.roleId !== assignment.originalRoleId && !assignment.isProcessing
    );

    if (changedAssignments.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'There are no role changes to save.'
      });
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();

    const updateRequests = changedAssignments.map(assignment =>
      this.projectService.editProjectUser(this.projectId, assignment.userId, assignment.roleId).pipe(
        map(success => ({
          assignment,
          success: !!success
        })),
        catchError((error) => {
          console.error('Error updating user role:', assignment.userId, error);
          return of({
            assignment,
            success: false
          });
        })
      )
    );

    forkJoin(updateRequests).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (results) => {
        const succeeded = results.filter(result => result.success);
        const failed = results.filter(result => !result.success);

        succeeded.forEach(result => {
          result.assignment.originalRoleId = result.assignment.roleId;
        });

        if (succeeded.length > 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Roles Updated',
            detail: `${succeeded.length} user role(s) updated successfully.`
          });
          this.notifyProjectUsersChanged();
        }

        if (failed.length > 0) {
          const failedEmails = failed.map(result => result.assignment.email).join(', ');
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: `Failed to update ${failed.length} user(s): ${failedEmails}`
          });
        }
      },
      error: (error) => {
        console.error('Error saving role changes:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save role changes. Please try again.'
        });
      }
    });
  }

  getRoleName(roleId: number): string {
    const role = this.roles.find(r => r.roleId === roleId);
    return role ? role.roleName : 'Unknown';
  }

  // Edit mode only — saves single user role change
  save(): void {
    if (!this.isEditMode || !this.editUser) return;

    if (!this.editUser.roleId || this.editUser.roleId <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Data',
        detail: 'Please select a role'
      });
      return;
    }

    this.isSaving = true;

    this.projectService.editProjectUser(this.projectId, this.editUser.userId!, this.editUser.roleId).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User role updated successfully'
          });
          this.notifyProjectUsersChanged();
          this.ref.close(true);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update user role'
          });
        }
      },
      error: (error) => {
        console.error('Error updating user role:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update user role'
        });
      }
    });
  }

  cancel(): void {
    this.ref.close();
  }

  private notifyProjectUsersChanged(): void {
    this.projectStateService.notifyProjectUsersRefresh(this.projectId);
    this.projectStateService.notifyProjectListRefresh();
  }

  private normalize(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private getAllowedRoleNamesForUser(userTypeName?: string, appRoleName?: string): string[] {
    const normalizedUserType = this.normalize(userTypeName);
    const normalizedAppRole = this.normalize(appRoleName);

    if (normalizedUserType === 'tmeic') {
      if (['super admin', 'admin', 'project admin'].includes(normalizedAppRole)) {
        return ['admin', 'user', 'readonly'];
      }

      if (normalizedAppRole === 'user') {
        return ['user', 'readonly'];
      }
    }

    if (normalizedUserType === 'asp') {
      return ['user', 'readonly'];
    }

    if (normalizedUserType === 'integrator') {
      return ['integrators'];
    }

    if (normalizedUserType === 'customer') {
      return ['customer'];
    }

    return [];
  }

  private findAppUserById(userId: number): UserModel | undefined {
    return this.allUsers.find(user => user.userId === userId);
  }

  getAssignableRolesForUser(userId: number, currentRoleId?: number): RoleModel[] {
    const user = this.findAppUserById(userId);
    const allowedRoleNames = this.getAllowedRoleNamesForUser(user?.userTypeName, user?.roleName);
    const allowedRoles = this.roles.filter(role => allowedRoleNames.includes(this.normalize(role.roleName)));

    if (currentRoleId && !allowedRoles.some(role => role.roleId === currentRoleId)) {
      const currentRole = this.roles.find(role => role.roleId === currentRoleId);
      if (currentRole) {
        return [...allowedRoles, currentRole];
      }
    }

    return allowedRoles;
  }
}
